import { useEffect, useMemo, useState } from "react";
import { fmtDate } from "@/lib/dates";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarCheck, Plus, Users, Check, ArrowLeft, Settings2, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SessionPresetsDialog } from "@/components/SessionPresetsDialog";
import { AttendeesDialog } from "@/components/AttendeesDialog";
import { fmtDateDay } from "@/lib/dates";

const SERVICE_TYPES = ["Sunday Service", "Midweek Bible Study", "Discipleship Training"];
const TYPE_TINT = { "Sunday Service": "#2D5A27", "Midweek Bible Study": "#D97706", "Discipleship Training": "#0284C7" };

export default function Attendance() {
  const [sessions, setSessions] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null); // session being checked in
  const [present, setPresent] = useState([]);
  const [form, setForm] = useState({ service_type: "Sunday Service", title: "Sunday Morning Service", date: new Date().toISOString().slice(0, 10) });
  const [otherMode, setOtherMode] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [catId, setCatId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const { user } = useAuth();
  const today = () => new Date().toLocaleDateString("en-CA");

  const loadPresets = () => api.get("/session-presets").then((r) => setPresets(r.data || [])).catch(() => {});
  const openPreset = (p) => {
    setForm({ service_type: p.service_type, title: p.title || p.label, date: today() });
    setOtherMode(!SERVICE_TYPES.includes(p.service_type));
    setOpen(true);
  };

  const openNew = () => {
    setForm({ service_type: "Sunday Service", title: "Sunday Morning Service", date: new Date().toISOString().slice(0, 10) });
    setOtherMode(false);
    setOpen(true);
  };

  const load = () => {
    api.get("/attendance").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadPresets(); }, []);

  const activeMembers = useMemo(() => members.filter((m) => m.status !== "inactive"), [members]);

  const createSession = async () => {
    if (!form.service_type.trim()) { toast.error("Please enter a gathering type"); return; }
    try {
      const { data } = await api.post("/attendance", { ...form, title: form.title.trim() || form.service_type.trim(), present_member_ids: [] });
      toast.success("Session created");
      setOpen(false);
      load();
      openCheckin({ ...data, present_member_ids: [] });
    } catch { toast.error("Could not create session"); }
  };

  const openCheckin = (s) => { setActive(s); setPresent(s.present_member_ids || []); };

  const toggle = (id) => setPresent((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const saveCheckin = async () => {
    try {
      await api.put(`/attendance/${active.id}`, { present_member_ids: present });
      toast.success(`Attendance saved — ${present.length} present`);
      setActive(null);
      load();
    } catch { toast.error("Could not save"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the ${s.title} session on ${fmtDate(s.date)}?`)) return;
    try { await api.delete(`/attendance/${s.id}`); toast.success("Session removed"); setViewing(null); load(); }
    catch { toast.error("Could not remove"); }
  };

  const presetTypes = new Set(presets.map((p) => p.service_type));
  const otherSessions = sessions.filter((s) => !presetTypes.has(s.service_type));
  const cats = [...presets, ...(otherSessions.length ? [{ id: "__other__", label: "Other", service_type: null }] : [])];
  const cat = cats.find((c) => c.id === catId) || cats[0];
  const catSessions = (c) => (c.id === "__other__" ? otherSessions : sessions.filter((s) => s.service_type === c.service_type));
  const shown = cat ? [...catSessions(cat)].sort((a, b) => (b.date || "").localeCompare(a.date || "")) : [];

  // Check-in view
  if (active) {
    return (
      <div className="space-y-6" data-testid="attendance-checkin-view">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setActive(null)} data-testid="checkin-back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="font-serif text-2xl font-semibold text-[#1C241B]">{active.title}</h2>
              <p className="text-sm text-[#7A8A78]">{active.service_type} · {fmtDate(active.date)}</p>
            </div>
          </div>
          <Button onClick={saveCheckin} data-testid="save-checkin-button" className="h-11 bg-[#2D5A27] hover:bg-[#23471F] text-white">
            Save ({present.length})
          </Button>
        </div>

        <Card className="p-5 bg-[#EAF2E8] border border-[#cfe3cb]">
          <p className="text-sm text-[#2D5A27] font-medium">
            {present.length} of {activeMembers.length} members marked present
          </p>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {activeMembers.map((m) => {
            const on = present.includes(m.id);
            return (
              <button key={m.id} onClick={() => toggle(m.id)}
                data-testid={`attendance-checkin-toggle-${m.id}`}
                className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${on ? "bg-[#2D5A27] border-[#2D5A27] text-white" : "bg-white border-[#E5E0D8] hover:border-[#2D5A27]"}`}>
                <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold ${on ? "bg-white/20 text-white" : "text-white"}`}
                  style={!on ? { backgroundColor: m.avatar_color || "#2D5A27" } : {}}>
                  {on ? <Check className="h-5 w-5" /> : `${m.first_name?.[0]}${m.last_name?.[0]}`}
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{m.first_name} {m.last_name}</p>
                  <p className={`text-xs truncate ${on ? "text-white/70" : "text-[#7A8A78]"}`}>{m.family_name}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Category view
  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-[#526050]">{sessions.length} recorded gatherings · choose a category</p>
        {user?.role === "admin" && (
          <Button variant="outline" onClick={() => setPresetsOpen(true)} data-testid="manage-presets-button"
            className="ml-auto border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]"><Settings2 className="h-4 w-4 mr-2" />Settings</Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="attendance-categories">
        {cats.map((c) => {
          const on = cat?.id === c.id;
          return (
            <button key={c.id} type="button" onClick={() => setCatId(c.id)} aria-pressed={on} data-testid={`category-button-${c.id}`}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                on ? "bg-[#2D5A27] border-[#2D5A27] text-white" : "bg-white border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]"}`}>
              {c.label}
              <span className={`rounded-full px-2 py-0.5 text-xs ${on ? "bg-white/20" : "bg-[#EAF2E8]"}`}>{catSessions(c).length}</span>
            </button>
          );
        })}
        {cats.length === 0 && <p className="text-sm text-[#7A8A78]">No categories yet{user?.role === "admin" ? " — add some in Settings." : "."}</p>}
      </div>

      {cat && (
        <Card className="bg-white border border-[#E5E0D8] shadow-sm overflow-hidden" data-testid="category-sessions">
          <div className="flex flex-wrap items-center gap-3 p-5 border-b border-[#E5E0D8]">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: TYPE_TINT[cat.service_type] || "#2D5A27" }}>
              <CalendarCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-serif text-xl font-semibold text-[#1C241B]" data-testid="category-title">{cat.label}</h2>
              <p className="text-xs text-[#7A8A78]">{shown.length} session{shown.length === 1 ? "" : "s"}{cat.service_type ? ` · ${cat.service_type}` : " · types without a category"}</p>
            </div>
            <Button data-testid="new-session-button" onClick={() => (cat.id === "__other__" ? openNew() : openPreset(cat))}
              className="ml-auto h-10 bg-[#2D5A27] hover:bg-[#23471F] text-white"><Plus className="h-4 w-4 mr-2" /> New Session</Button>
          </div>
          <ul className="divide-y divide-[#E5E0D8]">
            {shown.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => setViewing(s)} data-testid={`session-row-${s.id}`}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-[#F7F5F0] transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1C241B]">{fmtDateDay(s.date)}</p>
                    <p className="text-xs text-[#7A8A78] truncate">{s.title}{cat.id === "__other__" ? ` · ${s.service_type}` : ""}</p>
                  </div>
                  <span className="flex items-center gap-1 text-sm font-medium text-[#2D5A27]"><Users className="h-4 w-4" />{s.count}</span>
                  <ChevronRight className="h-4 w-4 text-[#7A8A78]" />
                </button>
              </li>
            ))}
            {shown.length === 0 && <li className="p-10 text-center text-[#7A8A78]">No sessions yet — click New Session to record one.</li>}
          </ul>
        </Card>
      )}

      <AttendeesDialog session={viewing} members={members} onClose={() => setViewing(null)}
        onEdit={(s) => { setViewing(null); openCheckin(s); }} onDelete={remove} />
      <SessionPresetsDialog open={presetsOpen} onOpenChange={setPresetsOpen} presets={presets}
        types={[...new Set([...SERVICE_TYPES, ...sessions.map((s) => s.service_type)])]} onChanged={loadPresets} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="session-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">New Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Gathering type</Label>
              <Select
                value={otherMode ? "__other__" : form.service_type}
                onValueChange={(v) => {
                  if (v === "__other__") { setOtherMode(true); setForm({ ...form, service_type: "", title: "" }); }
                  else { setOtherMode(false); setForm({ ...form, service_type: v, title: v === "Sunday Service" ? "Sunday Morning Service" : v }); }
                }}>
                <SelectTrigger data-testid="session-type-select"><SelectValue placeholder="Select a gathering type" /></SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  <SelectItem value="__other__">Other (type your own)…</SelectItem>
                </SelectContent>
              </Select>
              {otherMode && (
                <Input data-testid="session-type-custom-input" placeholder="e.g. Youth Night, Prayer Meeting"
                  value={form.service_type}
                  onChange={(e) => setForm({ ...form, service_type: e.target.value, title: e.target.value })}
                  className="mt-2" autoFocus />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="session-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" data-testid="session-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createSession} data-testid="create-session-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
              Create & Check-in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
